import { ButtonHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import { Loader2 } from 'lucide-react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  isLoading?: boolean
  loadingText?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    isLoading, 
    loadingText,
    icon,
    iconPosition = 'left',
    children, 
    disabled, 
    ...props 
  }, ref) => {
    
    const variants = {
      primary: 'btn btn-primary',
      secondary: 'btn btn-secondary',
      outline: 'btn btn-outline',
      ghost: 'btn btn-ghost',
      danger: 'btn btn-danger',
      success: 'btn btn-success',
      warning: 'btn btn-warning'
    }
    
    const sizes = {
      xs: 'btn-xs',
      sm: 'btn-sm',
      md: '',
      lg: 'btn-lg',
      xl: 'btn-xl'
    }

    const content = isLoading ? (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        {loadingText || children}
      </>
    ) : (
      <>
        {icon && iconPosition === 'left' && (
          <span className="rtl:ml-2 ltr:mr-2">{icon}</span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="rtl:mr-2 ltr:ml-2">{icon}</span>
        )}
      </>
    )

    return (
      <button
        className={clsx(
          variants[variant],
          sizes[size],
          isLoading && 'cursor-wait',
          className
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {content}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button